import { Application, Container, Polygon, Rectangle, type FederatedPointerEvent, type Sprite } from 'pixi.js';
import { hasDeeds, type Zone, type ZoneExit } from '@bratva/content';
import {
  findPath,
  isWalkable,
  nearestWalkable,
  type Grid,
  type Patrol,
  type Point,
  type SpawnedCar,
} from '@bratva/rules';
import { depth, gridToCenter, screenToGrid } from './iso.js';
import {
  carHitArea,
  drawCar,
  drawExit,
  drawExitLabel,
  drawGround,
  drawObject,
  drawObjectLabel,
  drawPatrol,
  drawPlayer,
  drawShadow,
  drawTileMarker,
  objectHitArea,
} from './placeholders.js';
import { loadSprites, objectSprite, tileSprite } from './sprites.js';
import { buildGrid } from './zoneGrid.js';

/** Спрайт в контейнере с нужной глубиной: остальная сцена работает с контейнерами. */
function wrap(sprite: Sprite, zIndex: number, shadow = false): Container {
  const container = new Container();
  container.zIndex = zIndex;
  // Тень рисуется первой, чтобы фигура на ней стояла, а не парила.
  if (shadow) container.addChild(drawShadow());
  container.addChild(sprite);
  return container;
}

/** Объект, к которому подошёл пацан: дом, ларёк или чужая тачка. */
export interface SceneTarget {
  kind: string;
  /** Устойчивый идентификатор для пометки «уже трогал». */
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  label?: string;
}

export interface SceneCallbacks {
  /**
    * Минуты списываются в момент решения, а не по прибытии.
    * Сцена отдаёт стоимость пути в клетках — во сколько минут она обойдётся,
    * решает игра: сет «Адидас» режет дорогу.
    */
  onWalk(to: Point, pathCost: number): void;
  /** Дошёл до клетки-выхода: дальше решает состояние, а не сцена. */
  onExit(exit: ZoneExit): void;
  /** Подошёл к объекту вплотную — интерфейс показывает, что тут можно. */
  onReach(target: SceneTarget): void;
  /** День кончился или стресс за 90 — ходить нельзя. */
  canWalk(): boolean;
}

const TILES_PER_SECOND = 3.2;
const MIN_ZOOM = 0.55;
const MAX_ZOOM = 1.8;

export class Scene {
  private readonly app = new Application();
  private readonly world = new Container();
  private readonly markers = new Container();
  private readonly player = new Container();
  private readonly hover = drawTileMarker(0xffffff, 0.18);

  private grid!: Grid;
  private path: Point[] = [];
  private pos = { x: 0, y: 0 };
  private zoom = 1;
  private destroyed = false;
  private inited = false;
  private carNodes: Container[] = [];
  private pendingCars: readonly SpawnedCar[] = [];
  private pendingTarget: SceneTarget | null = null;
  private pendingPatrols: readonly Patrol[] = [];
  private patrolNodes = new Map<string, { node: Container; x: number; y: number; to: Point; attention: number; hunting: boolean }>();

  constructor(
    private readonly host: HTMLElement,
    private readonly zone: Zone,
    private readonly cb: SceneCallbacks,
  ) {}

  async init(startTile: Point): Promise<void> {
    this.grid = buildGrid(this.zone);
    this.pos = { ...startTile };

    await this.app.init({
      background: '#15161a',
      antialias: true,
      resizeTo: this.host,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
    });
    if (this.destroyed) {
      this.app.destroy(true);
      return;
    }
    this.host.appendChild(this.app.canvas);
    this.inited = true;

    // Спрайты подтягиваются один раз; не доехали — сцена рисует коробки.
    await loadSprites();
    if (this.destroyed) {
      this.app.destroy(true, { children: true });
      return;
    }

    const ground = drawGround(this.zone);
    ground.zIndex = -1_000_000;

    this.world.sortableChildren = true;
    this.markers.zIndex = -999_999;
    this.world.addChild(ground, this.markers, this.hover);
    this.hover.zIndex = -999_998;

    for (const obj of this.zone.objects) {
      const sprite = objectSprite(obj);
      const node = sprite ? wrap(sprite, depth(obj.x + obj.w - 1, obj.y + obj.h - 1)) : drawObject(obj);
      if (hasDeeds(obj.kind)) {
        this.makeClickable(node, objectHitArea(obj), {
          kind: obj.kind,
          id: `${this.zone.id}:${obj.kind}:${obj.x}-${obj.y}`,
          x: obj.x,
          y: obj.y,
          w: obj.w,
          h: obj.h,
          label: obj.label,
        });
      }
      this.world.addChild(node);
      if (obj.label) this.world.addChild(drawObjectLabel(obj));
    }
    for (const exit of this.zone.exits) {
      this.world.addChild(drawExit(exit), drawExitLabel(exit));
    }

    // Игрок — спрайт, если он есть, иначе капсула из заглушек.
    const playerSprite = tileSprite('player', -0.5, -0.5);
    if (playerSprite) this.player.addChild(drawShadow(14, 7));
    this.player.addChild(playerSprite ?? drawPlayer());
    this.player.zIndex = depth(this.pos.x, this.pos.y);
    this.world.addChild(this.player);
    this.app.stage.addChild(this.world);

    this.renderCars(this.pendingCars);
    this.renderPatrols(this.pendingPatrols);
    this.bindInput();
    this.app.ticker.add((ticker) => this.tick(ticker.deltaMS / 1000));
    this.syncPlayerSprite();
    this.centerCamera(true);
  }

  /** Расстановка тачек на текущий день: за ночь она меняется. */
  renderCars(cars: readonly SpawnedCar[]): void {
    // init асинхронный: расстановка может приехать раньше рендерера,
    // тогда она ждёт своей очереди, а не теряется.
    this.pendingCars = cars;
    if (!this.inited) return;
    for (const node of this.carNodes) node.destroy({ children: true });
    this.carNodes = [];
    for (const car of cars) {
      const carSprite = tileSprite('car', car.x, car.y);
      const node = carSprite ? wrap(carSprite, depth(car.x, car.y), true) : drawCar(car);
      this.makeClickable(node, carHitArea(car), {
        kind: 'car',
        id: car.id,
        x: car.x,
        y: car.y,
        w: 1,
        h: 1,
        label: 'Тачка',
      });
      this.carNodes.push(node);
      this.world.addChild(node);
    }
  }

  /**
   * Патрули — функция игровой минуты, поэтому при каждом действии они
   * оказываются в новом месте. Доезжают плавно, иначе выглядит как телепорт.
   */
  renderPatrols(patrols: readonly Patrol[]): void {
    this.pendingPatrols = patrols;
    if (!this.inited) return;

    const seen = new Set<string>();
    for (const patrol of patrols) {
      seen.add(patrol.id);
      const existing = this.patrolNodes.get(patrol.id);
      // Круг внимания перерисовывается только когда он реально изменился.
      if (existing && existing.attention === patrol.attention && existing.hunting === patrol.hunting) {
        existing.to = { x: patrol.x, y: patrol.y };
        continue;
      }
      existing?.node.destroy({ children: true });

      const patrolSprite = tileSprite('patrol', -0.5, -0.5);
      const node = drawPatrol(patrol.attention, patrol.hunting, patrolSprite);
      this.world.addChild(node);
      this.patrolNodes.set(patrol.id, {
        node,
        x: existing?.x ?? patrol.x,
        y: existing?.y ?? patrol.y,
        to: { x: patrol.x, y: patrol.y },
        attention: patrol.attention,
        hunting: patrol.hunting,
      });
    }

    for (const [id, entry] of this.patrolNodes) {
      if (seen.has(id)) continue;
      entry.node.destroy({ children: true });
      this.patrolNodes.delete(id);
    }
    this.syncPatrolSprites(1);
  }

  /** Телепорт: загрузка сейва, сон, переход зоны. */
  setTile(tile: Point): void {
    this.path = [];
    this.pos = { ...tile };
    // init асинхронный: до появления рендерера трогать сцену нельзя,
    // позиция подхватится при инициализации.
    if (!this.inited) return;
    this.markers.removeChildren();
    this.syncPlayerSprite();
    this.centerCamera(true);
  }

  destroy(): void {
    this.destroyed = true;
    // StrictMode в dev монтирует эффект дважды: если init ещё не дошёл до конца,
    // он сам уберёт за собой по флагу destroyed.
    if (!this.inited) return;
    this.app.destroy(true, { children: true });
  }

  private bindInput(): void {
    const stage = this.app.stage;
    stage.eventMode = 'static';
    stage.hitArea = new Rectangle(0, 0, this.app.screen.width, this.app.screen.height);
    this.app.renderer.on('resize', (width: number, height: number) => {
      stage.hitArea = new Rectangle(0, 0, width, height);
    });

    stage.on('pointertap', (event: FederatedPointerEvent) => this.handleClick(event));
    stage.on('pointermove', (event: FederatedPointerEvent) => this.handleHover(event));

    this.app.canvas.addEventListener(
      'wheel',
      (event) => {
        event.preventDefault();
        const next = this.zoom * (event.deltaY > 0 ? 0.9 : 1.1);
        this.zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
        this.world.scale.set(this.zoom);
      },
      { passive: false },
    );
  }

  private toTile(event: FederatedPointerEvent): Point | null {
    const local = this.world.toLocal(event.global);
    const raw = screenToGrid(local.x, local.y);
    const tile = { x: Math.floor(raw.x), y: Math.floor(raw.y) };
    return tile.x >= 0 && tile.y >= 0 && tile.x < this.zone.size.width && tile.y < this.zone.size.height
      ? tile
      : null;
  }

  private handleHover(event: FederatedPointerEvent): void {
    const tile = this.toTile(event);
    if (!tile) {
      this.hover.visible = false;
      return;
    }
    const center = gridToCenter(tile.x, tile.y);
    this.hover.visible = true;
    this.hover.position.set(center.x, center.y - 16);
    this.hover.alpha = isWalkable(this.grid, tile.x, tile.y) ? 1 : 0.35;
  }

  private handleClick(event: FederatedPointerEvent): void {
    if (!this.cb.canWalk()) return;
    const clicked = this.toTile(event);
    if (!clicked) return;

    // Ткнул в дом — идём к ближайшей свободной клетке рядом, а не никуда.
    const target = nearestWalkable(this.grid, clicked, 4);
    if (!target) return;

    const from = { x: Math.round(this.pos.x), y: Math.round(this.pos.y) };
    const result = findPath(this.grid, from, target);
    if (result.points.length === 0) return;

    this.path = result.points;
    this.pendingTarget = null;
    this.drawPathPreview();
    this.cb.onWalk(this.path.at(-1)!, result.cost);
  }

  private drawPathPreview(): void {
    this.markers.removeChildren();
    for (const point of this.path) {
      const marker = drawTileMarker(0xa6123c, 0.28);
      const center = gridToCenter(point.x, point.y);
      marker.position.set(center.x, center.y - 16);
      this.markers.addChild(marker);
    }
  }

  private tick(dt: number): void {
    if (this.path.length > 0) {
      const next = this.path[0]!;
      const dx = next.x - this.pos.x;
      const dy = next.y - this.pos.y;
      const distance = Math.hypot(dx, dy);
      const step = TILES_PER_SECOND * dt;

      if (distance <= step) {
        this.pos = { x: next.x, y: next.y };
        this.path.shift();
        this.markers.children[0]?.destroy();
        if (this.path.length === 0) this.checkArrival();
      } else {
        this.pos = { x: this.pos.x + (dx / distance) * step, y: this.pos.y + (dy / distance) * step };
      }
      this.syncPlayerSprite();
    }
    this.syncPatrolSprites(Math.min(1, dt * 3));
    this.centerCamera(false);
  }

  /**
   * Клик по самому объекту, а не по клетке под ним: у высокого дома курсор
   * попадает в крышу, поэтому ловим по силуэту и гасим всплытие до сцены.
   */
  private makeClickable(node: Container, hitArea: Polygon, target: SceneTarget): void {
    node.eventMode = 'static';
    node.cursor = 'pointer';
    node.hitArea = hitArea;
    node.on('pointertap', (event: FederatedPointerEvent) => {
      event.stopPropagation();
      this.approach(target);
    });
  }

  /** Подойти вплотную: выбираем ближайшую свободную клетку по периметру. */
  private approach(target: SceneTarget): void {
    if (!this.cb.canWalk()) return;

    const from = { x: Math.round(this.pos.x), y: Math.round(this.pos.y) };
    const candidates: Point[] = [];
    for (let x = target.x - 1; x <= target.x + target.w; x++) {
      for (let y = target.y - 1; y <= target.y + target.h; y++) {
        const insideFootprint = x >= target.x && x < target.x + target.w && y >= target.y && y < target.y + target.h;
        if (insideFootprint || !isWalkable(this.grid, x, y)) continue;
        candidates.push({ x, y });
      }
    }
    candidates.sort((a, b) => Math.hypot(a.x - from.x, a.y - from.y) - Math.hypot(b.x - from.x, b.y - from.y));

    // Уже стоим рядом — открываем сразу, ходить некуда.
    if (candidates.some((tile) => tile.x === from.x && tile.y === from.y)) {
      this.cb.onReach(target);
      return;
    }

    for (const tile of candidates) {
      const result = findPath(this.grid, from, tile);
      if (result.points.length === 0) continue;
      this.path = result.points;
      this.pendingTarget = target;
      this.drawPathPreview();
      this.cb.onWalk(this.path.at(-1)!, result.cost);
      return;
    }
  }

  /** Дошёл до края зоны — дальше дорога, а её стоимость считает состояние. */
  private checkArrival(): void {
    const target = this.pendingTarget;
    this.pendingTarget = null;
    if (target) {
      this.cb.onReach(target);
      return;
    }

    const x = Math.round(this.pos.x);
    const y = Math.round(this.pos.y);
    const exit = this.zone.exits.find(
      (candidate) =>
        x >= candidate.x && x < candidate.x + candidate.w && y >= candidate.y && y < candidate.y + candidate.h,
    );
    if (exit) this.cb.onExit(exit);
  }

  private syncPatrolSprites(weight: number): void {
    for (const entry of this.patrolNodes.values()) {
      entry.x += (entry.to.x - entry.x) * weight;
      entry.y += (entry.to.y - entry.y) * weight;
      const center = gridToCenter(entry.x, entry.y);
      entry.node.position.set(center.x, center.y);
      entry.node.zIndex = depth(entry.x, entry.y);
    }
  }

  private syncPlayerSprite(): void {
    const center = gridToCenter(this.pos.x, this.pos.y);
    this.player.position.set(center.x, center.y);
    this.player.zIndex = depth(this.pos.x, this.pos.y) + 1;
  }

  private centerCamera(instant: boolean): void {
    if (!this.inited) return;
    const center = gridToCenter(this.pos.x, this.pos.y);
    const targetX = this.app.screen.width / 2 - center.x * this.zoom;
    const targetY = this.app.screen.height / 2 - center.y * this.zoom;
    if (instant) {
      this.world.position.set(targetX, targetY);
      return;
    }
    this.world.position.set(
      this.world.position.x + (targetX - this.world.position.x) * 0.12,
      this.world.position.y + (targetY - this.world.position.y) * 0.12,
    );
  }
}
